
import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Card,
    Stack,
    Skeleton,
    Tooltip,
    Typography,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import MasterCommonTable from "../../sharedComponents/MaterCommonTable/MasterCommonTable";
import { getAllTimeSheetData } from "../../services/taskManagement.service";
import { ISSUETYPE, PRIORITY, STATUS } from "../../common/constant/constant";
import bug from "../../assets/images/bug.png";
import { DatePicker } from "antd";
import {
    TextField,
    Autocomplete,
    Grid,
    Button,
} from "@mui/material";
import AddTaskModal from "./TimeSheetAdd";
import { IoAddCircleOutline } from "react-icons/io5";
import { getAllUsers } from "../../services/user.service";
import { getAllProject } from "../../services/reports.service";
import { useAuth } from "../../context/AuthProvider";
import { jwtDecode } from "jwt-decode";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { debounce } from "../../utils/debounce";
import moment from "moment";
import { isAdminRole } from "../../utils/roles";

const { RangePicker } = DatePicker;

const TimeSheet = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [tableTotalCount, setTableTotalCount] = useState(0);
    const [tasks, setTasks] = useState([]);
    const [addTaskOpen, setAddTaskOpen] = useState(false);

    // Filter State
    const [dateRange, setDateRange] = useState({
        startDate: dayjs().startOf("day"),
        endDate: dayjs().endOf("day"),
    });
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState({ _id: "All", name: "All" });
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState({ _id: "All", name: "All" });
    const [selectedIssueType, setSelectedIssueType] = useState({ value: "All", label: "All" });
    const [isAdmin, setIsAdmin] = useState(false);
    const [currentUserId, setCurrentUserId] = useState(null);

    // Initial query params for pagination only
    const [queryParam, setQueryParam] = useState({
        page: 1,
        limit: 10,
        search: "",
    });

    const issueTypes = [
        { value: "All", label: "All" },
        { value: ISSUETYPE.TESTING, label: "Testing" },
        { value: ISSUETYPE.DESIGNING, label: "Designing" },
        { value: ISSUETYPE.OTHERS, label: "Others" },
        { value: ISSUETYPE.MEETING, label: "Meeting" },
        { value: ISSUETYPE.DEPLOYMENT, label: "Deployment" },
    ];

    const statusOptions = [
        { value: STATUS.PENDING, label: "Pending" },
        { value: STATUS.TODO, label: "To Do" },
        { value: STATUS.INPROGRESS, label: "InProgress" },
        { value: STATUS.QA, label: "QA" },
        { value: STATUS.UAT, label: "UAT" },
        { value: STATUS.PRODUCTION, label: "Production" },
    ];

    const getLabelByValue = (value, options) => {
        const option = options.find((opt) => opt.value === value);
        return option ? option.label : "Unknown";
    };

    const issueTypeMap = {
        [ISSUETYPE.TESTING]: "Testing",
        [ISSUETYPE.DESIGNING]: "Designing",
        [ISSUETYPE.OTHERS]: "Others",
        [ISSUETYPE.MEETING]: "Meeting",
        [ISSUETYPE.DEPLOYMENT]: "Deployment",
        [ISSUETYPE.BUG]: "Bug",
        [ISSUETYPE.TASK]: "Task",
    };

    const formatDuration = (ms) => {
        if (!ms) return "0h 0m";
        const totalMinutes = Math.floor(ms / 60000);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${hours}h ${minutes}m`;
    };

    const { accessToken } = useAuth();

    // Auth Effect
    useEffect(() => {
        if (accessToken) {
            try {
                const decodedToken = jwtDecode(accessToken);
                const isOverAllAdmin = isAdminRole(decodedToken.role);
                setIsAdmin(isOverAllAdmin);
                setCurrentUserId(decodedToken.id);

                if (isOverAllAdmin) {
                    // Fetch Users for Admin
                    getAllUsers({ isAdmin: true, page: 1, limit: 1000 }).then((userRes) => {
                        const userList = Array.isArray(userRes?.data?.data) ? userRes.data.data : [];
                        const formattedUsers = userList.map((u) => ({
                            ...u,
                            name: `${u.firstName}${u.lastName ? " " + u.lastName : ""}`,
                        }));
                        setUsers([{ _id: "All", name: "All" }, ...formattedUsers]);
                    }).catch(err => console.error("User Fetch Error", err));
                } else {
                    setSelectedUser({ _id: decodedToken.id, name: "Me" });
                }

            } catch (error) {
                console.error("Error decoding token:", error);
            }
        }
    }, [accessToken]);

    // Fetch Projects Effect
    useEffect(() => {
        getAllProject({ isEnabled: true }).then((projRes) => {
            const projectList = Array.isArray(projRes.data) ? projRes.data : [];
            setProjects([{ _id: "All", name: "All" }, ...projectList]);
        }).catch(err => console.error("Project Fetch Error", err));
    }, []);

    // Reset page to 1 on filter change
    useEffect(() => {
        setQueryParam(prev => ({ ...prev, page: 1 }));
    }, [dateRange, selectedUser, selectedProject, selectedIssueType]);

    const handleSearch = useCallback(
        debounce((value) => {
            setQueryParam((prev) => ({
                ...prev,
                search: value,
                page: 1, // Reset page on search
            }));
        }, 500),
        []
    );

    const fetchTimeSheetData = async () => {
        try {
            setLoading(true);

            const params = {
                page: queryParam.page,
                limit: queryParam.limit,
                search: queryParam.search,
                startDate: dateRange.startDate ? dateRange.startDate.startOf("day").toISOString() : null,
                endDate: dateRange.endDate ? dateRange.endDate.endOf("day").toISOString() : null,
                project: selectedProject._id !== "All" ? selectedProject._id : null,
                type: selectedIssueType.value !== "All" ? selectedIssueType.value : null,
            };

            if (isAdmin) {
                if (selectedUser._id !== "All") {
                    params.assignee = selectedUser._id;
                }
            } else if (currentUserId) {
                params.assignedTo = currentUserId;
            }

            const res = await getAllTimeSheetData(params);
            const data = res?.data?.data || [];
            const formatted = data.map((task) => {
                let workingTimeContent = formatDuration(task.workingTimeMs);

                // Add Tooltip for Testing Tasks
                if (task.issueType === ISSUETYPE.TESTING && task.testingTasks && task.testingTasks.length > 0) {
                    workingTimeContent = (
                        <Tooltip
                            title={
                                <Stack spacing={0.5} sx={{ p: 0.5 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.2)', mb: 0.5 }}>
                                        Linked Tasks:
                                    </Typography>
                                    {task.testingTasks.map((t) => (
                                        <Typography key={t._id} variant="caption" display="block">
                                            {t.taskNo} - {t.title}
                                        </Typography>
                                    ))}
                                </Stack>
                            }
                            arrow
                        >
                            <span style={{ borderBottom: '1px dashed #666', cursor: 'help' }}>
                                {workingTimeContent}
                            </span>
                        </Tooltip>
                    );
                }

                let startTime = "-";
                let endTime = "-";

                if (task.workLog && task.workLog.length > 0) {
                    const firstLog = task.workLog[0];
                    if (firstLog.startTime) startTime = moment(firstLog.startTime).format("hh:mm A");
                    if (firstLog.endTime) endTime = moment(firstLog.endTime).format("hh:mm A");
                }


                return {
                    ...task,
                    createdAt: moment(task.createdAt).format("DD-MM-YYYY"),
                    updatedAt: moment(task.updatedAt).format("DD-MM-YYYY"),
                    issueType: issueTypeMap[task.issueType] || "Unknown",
                    statusLabel: getLabelByValue(task.status, statusOptions),
                    assignee_info: task.assignee_info ? task.assignee_info.firstName + " " + task.assignee_info.lastName : "Unassigned",
                    projectName: task.project_info ? task.project_info.name : "Unassigned",
                    startTime,
                    endTime,
                    workingTime: workingTimeContent,
                };
            });

            setTasks(formatted);
            setTableTotalCount(res?.data?.totalCount || 0);
            console.log("totalCount", res?.data?.totalCount);
        } catch (error) {
            console.error("Error fetching time sheet data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTimeSheetData();
    }, [queryParam]);

    const columns = [
        { id: "taskNo", label: "TaskNo" },
        { id: "createdAt", label: "Created Date" },
        { id: "issueType", label: "Type" },
        ...(isAdmin ? [{ id: "assignee_info", label: "Assignee" }] : []),
        { id: "projectName", label: "Projects" },
        { id: "startTime", label: "Start Time" },
        { id: "endTime", label: "End Time" },
        { id: "workingTime", label: "Working Time" },
    ];


    const handleTableEvent = (event) => {
        setQueryParam((prev) => ({
            ...prev,
            page: event.page,
            limit: event.limit,
        }));
    };

    const tableConfig = {
        isAction: false,
        isView: true,
        isEdit: false,
        isDelete: false,
        isSearch: false,
        isAddNew: false,
    };

    const handleView = useCallback((task) => {
        navigate(`/home/taskDetails?taskId=${task._id}`);
    }, [navigate]);

    return (
        <Box sx={{ padding: "20px" }}>
            <Grid container spacing={2} mb={2} alignItems={"center"}>
                <Grid item>
                    <Stack>
                        <Box
                            className="SearchInputBoxContainer"
                            sx={{
                                margin: 0,
                                paddingLeft: 0,
                                display: "flex",
                                alignItems: "center",
                                backgroundColor: "#f7f7f7",
                                borderRadius: "4px",
                                border: "1px solid #ced4da",
                                height: "40px",
                                position: "relative",
                            }}
                        >
                            <input
                                className="SearchInputBox"
                                placeholder="Search Here"
                                onChange={(e) => handleSearch(e.target.value)}
                                style={{
                                    backgroundColor: "transparent",
                                    border: "none",
                                    outline: "none",
                                    paddingLeft: "10px",
                                    paddingRight: "40px",
                                    height: "100%",
                                    width: "200px",
                                    fontSize: "14px",
                                }}
                            />
                            <SearchOutlinedIcon
                                sx={{
                                    position: "absolute",
                                    right: "10px",
                                    color: "#6c757d",
                                }}
                            />
                        </Box>
                    </Stack>
                </Grid>
                <Grid item>
                    <RangePicker
                        value={[
                            dateRange.startDate ? dayjs(dateRange.startDate) : null,
                            dateRange.endDate ? dayjs(dateRange.endDate) : null,
                        ]}
                        onChange={(dates) => {
                            const [start, end] = dates || [];
                            if (start && end) {
                                setDateRange({
                                    startDate: dayjs(start).startOf("day"),
                                    endDate: dayjs(end).endOf("day"),
                                });
                            } else {
                                setDateRange({
                                    startDate: dayjs().startOf("day"),
                                    endDate: dayjs().endOf("day"),
                                });
                            }
                        }}
                        className="datePicker"
                        format={"DD-MM-YYYY"}
                        style={{ maxWidth: "220px" }}
                    />
                </Grid>
                {isAdmin && (
                    <Grid item>
                        <Autocomplete
                            options={users}
                            getOptionLabel={(u) => u.name || ""}
                            value={selectedUser}
                            onChange={(e, val) => setSelectedUser(val || { _id: "All", name: "All" })}
                            renderInput={(params) => (
                                <TextField {...params} label="Select User" size="small" />
                            )}
                            sx={{ width: 190 }}
                        />
                    </Grid>
                )}
                <Grid item>
                    <Autocomplete
                        options={projects}
                        getOptionLabel={(p) => p.name || ""}
                        value={selectedProject}
                        onChange={(e, val) => setSelectedProject(val || { _id: "All", name: "All" })}
                        renderInput={(params) => (
                            <TextField {...params} label="Select Project" size="small" />
                        )}
                        sx={{ width: 190 }}
                    />
                </Grid>
                <Grid item>
                    <Autocomplete
                        options={issueTypes}
                        getOptionLabel={(option) => option.label || ""}
                        value={selectedIssueType}
                        onChange={(e, val) => setSelectedIssueType(val || { value: "All", label: "All" })}
                        renderInput={(params) => (
                            <TextField {...params} label="Select Issue Type" size="small" />
                        )}
                        sx={{ width: 130 }}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                    />
                </Grid>
                <Grid item>
                    <Button
                        variant="contained"
                        startIcon={<IoAddCircleOutline size={20} />}
                        onClick={() => setAddTaskOpen(true)}
                        sx={{
                            backgroundColor: "#008080",
                            color: "white",
                            "&:hover": {
                                backgroundColor: "#006666",
                            },
                            height: "40px",
                            textTransform: "none",
                        }}
                    >
                        Add
                    </Button>
                </Grid>
            </Grid>

            <MasterCommonTable
                tableConfig={tableConfig}
                data={tasks}
                isServerSide={true}
                columns={columns}
                tableTotalCount={tableTotalCount}
                handleView={handleView}
                tableOutputEvent={handleTableEvent}
                openLoader={loading}
            />

            {addTaskOpen && (
                <AddTaskModal
                    open={addTaskOpen}
                    onClose={() => setAddTaskOpen(false)}
                    formType="add"
                    fetchTasks={fetchTimeSheetData}
                    GetData={fetchTimeSheetData}
                    defaultIssueType={
                        selectedIssueType.value !== "All" ? selectedIssueType.value : null
                    }
                />
            )}
        </Box>
    );
};

export default TimeSheet;
